import { Languages } from '@/constants'
import { useNotification, useStore } from '@/containers'
import { type Product } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { copyToClipboard } from '@/utils/next-util'
import Image from 'next/image'
import type { Dispatch, SetStateAction } from 'react'
import DrawBottom from '../DrawBottom'
import ImageOptimize from '../ImageOptimize'
export interface PopupShareType {
  data: Product
  isOpenShare: boolean
  setIsOpenShare: Dispatch<SetStateAction<boolean>>
}

export function PopupShare({ data, isOpenShare = false, setIsOpenShare }: PopupShareType) {
  const { selectedLang } = useStore()
  const { systemConfig } = useNotification()
  const currency = (lang: string) => {
    switch (lang) {
      case 'vi':
        return 'VND'
      case 'en':
        return 'VND'
      default:
        return 'VND'
    }
  }
  const percent = Math.ceil(100 - (data.finalPrice / data.price) * 100)
  const handleFacebookShare = (link: string) => {
    const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`
    window.open(facebookShareUrl, '_blank')
  }
  return (
    <DrawBottom open={isOpenShare} onClose={() => setIsOpenShare(false)} title={'Chia sẻ với bạn bè'}>
      <div className='listProductRelated flex h-full flex-col'>
        <div className='mb-4 flex gap-3 rounded bg-white-base p-2'>
          <ImageOptimize
            alt={getTranslate(data.name)}
            src={data.thumbnail || systemConfig?.imageWarehousePlaceholder || process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER || ''}
            width={80}
            priority
            height={80}
            className='h-full max-h-[80px] w-[80px] object-cover'
          />
          <div className='flex-1 text-sm font-medium leading-tight text-dark-900'>
            <p className='clamp-2'>{getTranslate(data.name)}</p>
            <div className='price'>
              <div className='flex-start flex items-center gap-x-1'>
                <p className={`text-sm leading-normal text-black-100 line-through`}>
                  {data.price &&
                    data.price
                      .toLocaleString(Languages.VN, {
                        style: 'currency',
                        currency: currency(selectedLang as string)
                      })
                      .replace(/\s₫/g, 'đ')}
                </p>
                {percent > 0 && (
                  <span className='discount flex w-max items-baseline gap-[4px] rounded bg-primary-base text-xs leading-5 text-stone-50'>
                    <span className='flex items-center px-[4px] text-white'>
                      <span className='value'>{'-' + percent}</span>
                      <span className='unit'>%</span>
                    </span>
                  </span>
                )}
              </div>
              <div className='sale-price flex items-center justify-between'>
                <p className={`text-xl font-bold leading-8 text-primary-base `}>
                  {data.finalPrice &&
                    data.finalPrice
                      .toLocaleString(Languages.VN, {
                        style: 'currency',
                        currency: currency(selectedLang as string)
                      })
                      .replace(/\s₫/g, 'đ')}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className='grid w-full grid-cols-2 gap-3'>
          <div className='relative w-full'>
            <div className='flex w-full flex-wrap justify-center'>
              <div className='mb-3 text-base font-bold leading-tight text-gray-800'>Chia sẻ bằng QR code</div>
              <div className='mb-3 flex w-full justify-center text-center'>
                <Image
                  src={`${process.env.NEXT_PUBLIC_TOOL_API_URL}/qr?margin=1&data=${window.location.href}`}
                  alt={'qrcode'}
                  width={128}
                  height={128}
                />
              </div>
              <DownloadButton
                imageUrl={`${process.env.NEXT_PUBLIC_TOOL_API_URL}/qr?margin=1&data=${window.location.href}`}
              />
            </div>
            <div className='absolute right-0 top-[50%] h-[110px] w-px translate-y-[-50%] bg-zinc-300'></div>
          </div>
          <div className='w-full'>
            <div className='flex w-full flex-wrap justify-center'>
              <div className='mb-3 text-base font-bold leading-tight text-gray-800'>Chia sẻ qua</div>
              <div className='buttonGroup mt-5 flex w-full flex-col items-center justify-center gap-2'>
                <button
                  onClick={() => handleFacebookShare(window.location.href)}
                  className='flex h-[40px] w-[140px] items-center justify-center gap-1 rounded bg-[#1877F2] px-2'
                >
                  <svg xmlns='http://www.w3.org/2000/svg' width={24} height={24} viewBox='0 0 31 31' fill='none'>
                    <g clipPath='url(#clip0_2495_30862)'>
                      <path
                        d='M30.7031 15.75C30.7031 23.2371 25.2176 29.4428 18.0469 30.5678V20.0859H21.542L22.207 15.75H18.0469V12.9363C18.0469 11.7498 18.6281 10.5938 20.4914 10.5938H22.3828V6.90234C22.3828 6.90234 20.666 6.60938 19.0248 6.60938C15.5988 6.60938 13.3594 8.68594 13.3594 12.4453V15.75H9.55078V20.0859H13.3594V30.5678C6.18867 29.4428 0.703125 23.2371 0.703125 15.75C0.703125 7.46602 7.41914 0.75 15.7031 0.75C23.9871 0.75 30.7031 7.46602 30.7031 15.75Z'
                        fill='#1877F2'
                      />
                      <path
                        d='M21.542 20.0859L22.207 15.75H18.0469V12.9363C18.0469 11.75 18.628 10.5938 20.4913 10.5938H22.3828V6.90234C22.3828 6.90234 20.6663 6.60938 19.0251 6.60938C15.5988 6.60938 13.3594 8.68594 13.3594 12.4453V15.75H9.55078V20.0859H13.3594V30.5677C14.1231 30.6875 14.9058 30.75 15.7031 30.75C16.5005 30.75 17.2832 30.6875 18.0469 30.5677V20.0859H21.542Z'
                        fill='white'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_2495_30862'>
                        <rect width={30} height={30} fill='white' transform='translate(0.703125 0.75)' />
                      </clipPath>
                    </defs>
                  </svg>

                  <span className='text-sm font-semibold leading-tight text-white'>Facebook</span>
                </button>
                <button
                  onClick={() => copyToClipboard(window.location.href)}
                  className='flex h-[40px] w-[140px] items-center justify-center gap-1 rounded border border-blue px-2'
                >
                  <svg xmlns='http://www.w3.org/2000/svg' width={16} height={16} viewBox='0 0 19 19' fill='none'>
                    <g clipPath='url(#clip0_2495_30884)'>
                      <path
                        d='M11.3012 7.12531C11.0879 6.91082 10.8515 6.7207 10.5963 6.55841C9.92029 6.12554 9.13441 5.89552 8.33169 5.89552C7.21754 5.89344 6.14867 6.33612 5.36222 7.12531L1.54691 10.9436C0.7618 11.7302 0.320528 12.796 0.319825 13.9074C0.318349 16.2266 2.19718 18.1078 4.51638 18.1093C5.6288 18.1132 6.69669 17.6726 7.48285 16.8855L10.6323 13.7361C10.6895 13.6794 10.7215 13.602 10.721 13.5214C10.7201 13.3557 10.585 13.2222 10.4193 13.2232H10.2993C9.64134 13.2255 8.98921 13.0991 8.37971 12.8513C8.2674 12.805 8.13833 12.8311 8.05278 12.9172L5.78817 15.1848C5.08496 15.888 3.94484 15.888 3.24163 15.1848C2.53842 14.4816 2.53842 13.3415 3.24163 12.6383L7.07195 8.81099C7.7746 8.10922 8.91286 8.10922 9.61551 8.81099C10.089 9.25666 10.8277 9.25666 11.3012 8.81099C11.5049 8.60709 11.6284 8.33664 11.6491 8.04912C11.671 7.70553 11.5443 7.36904 11.3012 7.12531Z'
                        fill='#1877F2'
                      />
                      <path
                        d='M17.0869 1.33937C15.447 -0.300623 12.788 -0.300623 11.148 1.33937L8.00156 4.48279C7.91517 4.56961 7.89024 4.70019 7.93858 4.81274C7.98634 4.92561 8.09798 4.99805 8.22051 4.9957H8.33148C8.98871 4.9945 9.6398 5.12185 10.2481 5.37064C10.3604 5.41687 10.4895 5.39078 10.5751 5.30467L12.8337 3.04906C13.5369 2.34585 14.677 2.34585 15.3802 3.04906C16.0834 3.75227 16.0834 4.89239 15.3802 5.5956L12.5667 8.40609L12.5427 8.43308L11.5589 9.41092C10.8562 10.1127 9.71797 10.1127 9.01532 9.41092C8.54178 8.96525 7.80314 8.96525 7.32964 9.41092C7.12461 9.61637 7.00099 9.88917 6.98169 10.1788C6.95975 10.5224 7.08647 10.8588 7.32964 11.1026C7.67681 11.4513 8.08301 11.7356 8.52941 11.9425C8.5924 11.9724 8.65539 11.9964 8.71838 12.0234C8.78137 12.0504 8.84734 12.0714 8.91033 12.0954C8.97332 12.1194 9.0393 12.1404 9.10228 12.1584L9.27926 12.2064C9.39923 12.2364 9.51923 12.2604 9.64219 12.2814C9.79031 12.3034 9.93949 12.3174 10.0891 12.3234H10.2991H10.3171L10.497 12.3024C10.563 12.2994 10.632 12.2844 10.71 12.2844H10.812L11.0189 12.2544L11.1149 12.2364L11.2889 12.2004H11.3219C12.0587 12.0154 12.7314 11.6339 13.2685 11.0966L17.0868 7.2783C18.7269 5.63831 18.7269 2.97936 17.0869 1.33937Z'
                        fill='#1877F2'
                      />
                    </g>
                    <defs>
                      <clipPath id='clip0_2495_30884'>
                        <rect width={18} height={18} fill='white' transform='translate(0.318359 0.109375)' />
                      </clipPath>
                    </defs>
                  </svg>
                  <div className='text-sm font-semibold leading-tight text-blue'>Sao chép link</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DrawBottom>
  )
}

export default PopupShare

const DownloadButton = ({ imageUrl }: { imageUrl: string }) => {
  const handleSaveImage = async () => {
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'image.png' // Tên tệp tin khi tải về (có thể thay đổi)
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error saving image:', error)
    }
  }
  return (
    <button onClick={handleSaveImage} className='flex h-9 items-center gap-2 rounded border border-zinc-300 px-2'>
      <svg xmlns='http://www.w3.org/2000/svg' width={21} height={21} viewBox='0 0 21 21' fill='none'>
        <path
          d='M17.75 15.4619V12.9619C17.75 12.7962 17.6842 12.6372 17.5669 12.52C17.4497 12.4028 17.2908 12.3369 17.125 12.3369C16.9592 12.3369 16.8003 12.4028 16.6831 12.52C16.5658 12.6372 16.5 12.7962 16.5 12.9619V15.4619C16.5 15.6277 16.4342 15.7866 16.3169 15.9039C16.1997 16.0211 16.0408 16.0869 15.875 16.0869H4.625C4.45924 16.0869 4.30027 16.0211 4.18306 15.9039C4.06585 15.7866 4 15.6277 4 15.4619V12.9619C4 12.7962 3.93415 12.6372 3.81694 12.52C3.69973 12.4028 3.54076 12.3369 3.375 12.3369C3.20924 12.3369 3.05027 12.4028 2.93306 12.52C2.81585 12.6372 2.75 12.7962 2.75 12.9619V15.4619C2.75 15.9592 2.94754 16.4361 3.29917 16.7877C3.65081 17.1394 4.12772 17.3369 4.625 17.3369H15.875C16.3723 17.3369 16.8492 17.1394 17.2008 16.7877C17.5525 16.4361 17.75 15.9592 17.75 15.4619ZM13.7625 12.1994L10.6375 14.6994C10.5272 14.7866 10.3906 14.834 10.25 14.834C10.1094 14.834 9.97284 14.7866 9.8625 14.6994L6.7375 12.1994C6.62364 12.0919 6.55426 11.9456 6.5431 11.7894C6.53195 11.6333 6.57982 11.4786 6.67725 11.356C6.77468 11.2334 6.91457 11.1519 7.06924 11.1275C7.22392 11.1032 7.3821 11.1377 7.5125 11.2244L9.625 12.9119V4.21191C9.625 4.04615 9.69085 3.88718 9.80806 3.76997C9.92527 3.65276 10.0842 3.58691 10.25 3.58691C10.4158 3.58691 10.5747 3.65276 10.6919 3.76997C10.8092 3.88718 10.875 4.04615 10.875 4.21191V12.9119L12.9875 11.2244C13.0504 11.165 13.125 11.1193 13.2065 11.0902C13.2881 11.0612 13.3747 11.0493 13.461 11.0555C13.5474 11.0617 13.6315 11.0857 13.708 11.1261C13.7846 11.1664 13.8519 11.2223 13.9058 11.29C13.9596 11.3578 13.9988 11.436 14.0209 11.5196C14.0429 11.6033 14.0474 11.6907 14.0339 11.7762C14.0204 11.8617 13.9894 11.9434 13.9426 12.0163C13.8959 12.0891 13.8346 12.1515 13.7625 12.1994Z'
          fill='black'
        />
      </svg>
      <span className='text-sm font-semibold leading-tight text-gray-800'>Tải QR code</span>
    </button>
  )
}
